(function (global, factory) {
  'use strict';

  if (typeof define === 'function' && define.amd) {
    // If supports for AMD is available return the value to the AMD Loader
    // and to the global (window) object in case any JS module is expecting
    // the SearchBarResultHandlerModule to exists on the window object.
    define('searchBarResultHandler', ['searchBarHelper'], (SearchBarHelper) => {
      global.SearchBarResultHandler = factory(global, SearchBarHelper);
      return global.SearchBarResultHandler;
    });
  } else if (typeof exports === 'object') {
    // If supports for Common JS is available export the module
    // and pass its value to the global (window) object in case non Common JS code
    // is expected to work with this SearchBarResultHandlerModule.
    const handler = factory(global, global.SearchBarHelper);
    global.SearchBarResultHandler = handler;
    module.exports = handler;
  } else {
    // If not support for AMD or Common JS is available register the module in the global scope.
    global.SearchBarResultHandler = factory(global, global.SearchBarHelper);
  }
}(typeof window !== 'undefined' ? window : {}, (global, SearchBarHelper) => {
  'use strict';

  global.NewHomeSource = global.NewHomeSource || {};
  const previousComponent = global.NewHomeSource.SearchBarResultHandler;
  /**
   * Helper to dispatch a GA4 event when a search bar result is clicked.
   * @param {HTMLElement} item The clicked result item element.
   * @param {string} searchText The current search term.
   */
  function trackGA4SearchResultClick(item) {
    if (!item) {
      return;
    }
    const title = item.querySelector('[data-title]').getAttribute('data-title');
    const locationUrl = item.querySelector('[data-location-url]').getAttribute('data-location-url');
    const eventName = 'searchterm_events';
    const clickTarget2 = item.getAttribute('data-ga4-event-click_target2');
    const ga4Event = {
      eventType: 'GA4Event',
      eventName,
      params: {
        search_value: title,
        destination: locationUrl,
        result_label: title,
        click_target: 'SearchBarResult',
        click_target2: clickTarget2
      }
    };
    document.body.dispatchEvent(new CustomEvent('trackOnDemandEvent', { detail: { data: ga4Event } }));
  }

  /**
   * SearchBarResultHandler
   * Handles search input interactions in a reusable way.
   * Provides methods for event binding, input handling, and noConflict support.
   * Expects a configuration object with the current search bar state.
   *
   * @param {Object} config - Configuration object.
   */
  class SearchBarResultHandler {
    /**
     * Creates a new SearchBarResultHandler instance.
     */
    constructor({
      searchText,
      searchBarContainer,
      isMobile,
      searchResults = []
    }) {
      this.searchBarState = { searchText, searchBarContainer, isMobile };
      this.searchBarContainer = this.searchBarState.searchBarContainer;
      this.searchResultsState = searchResults;

      this.formattedSearchText = null;
      this.observers = [];

      if (this.searchBarContainer) {
        this.bindMessageQuerySelector();
        this.bindSearchResultsQuerySelector();

        this.activeSearchResultItemClass = 'nhs-n1-c-search-bar-results__item--active';
      }

      this.toggleSearchResults(false);
      this.toggleSearchResultsMessageContainer(false);
      this.loadSearchResults();
      this.bindEvents();
    }

    /**
     * Binds query selectors for the message container and its elements.
     */
    bindMessageQuerySelector() {
      const querySelector = {
        resultsContainer: '[data-search-bar-result-container]',
        messageContainer: '[data-search-results-message-container]',
        dataSearchKeyword: '[data-search-keyword]',
        searchKeywordContainer: '[data-search-keyword-container]'
      };

      this.resultsContainer = this.searchBarContainer.querySelector(querySelector.resultsContainer);
      this.messageContainer = this.searchBarContainer.querySelector(querySelector.messageContainer);
      this.searchKeywordContainer = this.searchBarContainer.querySelector(querySelector.searchKeywordContainer);

      if (this.messageContainer) {
        this.dataSearchKeyword = this.messageContainer.querySelector(querySelector.dataSearchKeyword);
      }
    }

    /**
     * Binds query selectors for the search results and its elements.
     */
    bindSearchResultsQuerySelector() {
      this.totalResultsCount = this.resultsContainer.querySelector('[data-total-results-count]');
      this.totalLocationCount = this.resultsContainer.querySelector('[data-total-location-count]');
      this.totalCommunitiesCount = this.resultsContainer.querySelector('[data-total-communities-count]');
      this.totalBuildersCount = this.resultsContainer.querySelector('[data-total-builders-count]');
      this.totalArticlesCount = this.resultsContainer.querySelector('[data-total-articles-count]');

      this.searchResultTabs = this.resultsContainer.querySelectorAll('[data-search-result-tab]');
      this.tabLeftScrollButton = this.resultsContainer.querySelector('[data-tabs-scroll-left-button]');
      this.tabRightScrollButton = this.resultsContainer.querySelector('[data-tabs-scroll-right-button]');
      this.tabsContainer = this.resultsContainer.querySelector('[data-tabs-container]');
    }

    /**
     * Binds click events to each rendered search result item.
     */
    bindSearchResultsEvents() {
      this.searchResultsListContainer = this.resultsContainer.querySelector('[data-search-results-list]');
      this.searchBarResultItems = this.searchResultsListContainer.querySelectorAll('[data-search-bar-result-item]');

      SearchBarHelper.createListNavigator(this.searchBarContainer, {
        getItems: () => this.searchBarResultItems,
        activeClass: this.activeSearchResultItemClass,
        onEnter: (item) => {
          this.handleSearchResultItemClick({ currentTarget: item });
        }
      });
    }

    /**
     * Subscribes a callback function to be notified when the selected location changes.
     * @param {Function} callback - The callback function to be invoked on location change.
     */
    subscribeSelectLocationChange(callback) {
      this.observers.push(callback);
    }

    /**
     * Notifies all subscribed observers with the given value.
     * @param {*} value - The value to pass to the observers.
     */
    notifyObservers(value) {
      this.observers.forEach((callback) => callback(value));
    }

    /**
     * Sets the selected search text and notifies observers of the change.
     * @param {string} value - The selected search text.
     */
    setSelectedSearchText(value) {
      this.formattedSearchText = value;
      this.notifyObservers(value);
    }

    /**
     * Handles the click event on a search result item.
     * Navigates to the URL specified in the item's data-location-url attribute.
     * @param {Event} event - The click event object.
     */
    handleSearchResultItemClick(event) {
      const itemElement = event.currentTarget;
      const data = SearchBarResultHandler.extractItemClickData(itemElement);

      if (!data.url) {
        return;
      }

      trackGA4SearchResultClick(itemElement);
      const formattedText = SearchBarHelper.getFormattedSearchText(data);
      if (formattedText) {
        const personal = SearchBarHelper.getFormattedLocationParams(data);
        SearchBarHelper.updateLastSearchCookie(personal);

        this.setSelectedSearchText(formattedText);
      }

      this.toggleSearchResults(false, true);
      SearchBarResultHandler.navigateTo(data.url, data.opensInNewTab);
    }

    /**
     * Extracts the URL and new tab flag from a search result item element.
     * @param {HTMLElement} itemElement - The search result item element.
     * @returns {Object} An object containing the URL and new tab flag.
     */
    static extractItemClickData(itemElement) {
      const element = itemElement.querySelector('[data-location-url]');

      if (element) {
        return {
          url: element.getAttribute('data-location-url') || null,
          opensInNewTab: element.getAttribute('data-should-open-in-new-tab') === 'true',

          locationName: element.getAttribute('data-location-name') || '',
          locationType: element.getAttribute('data-location-type') || '',
          marketAreaName: element.getAttribute('data-market-area-name') || '',
          marketAreaId: element.getAttribute('data-market-area-id') || '',
          stateEquivalentAbbreviation: element.getAttribute('data-state-equivalent-abbreviation') || '',
          county: element.getAttribute('data-county') || '',
          postalCode: element.getAttribute('data-zip-code') || '',
          city: element.getAttribute('data-city') || '',
          schoolDistrict: element.getAttribute('data-school-district') || '',
          marketState: element.getAttribute('data-market-state') || ''
        };
      }
      return null;
    }

    /**
     * Navigates to the specified URL.
     * Opens in a new tab if the opensInNewTab flag is true; otherwise, navigates in the current tab.
     * @param {string} url - The URL to navigate to.
     * @param {boolean} opensInNewTab - Whether to open the URL in a new tab.
     */
    static navigateTo(url, opensInNewTab) {
      if (opensInNewTab) {
        window.open(url, '_blank');
      } else {
        window.location.href = url;
      }
    }

    /**
     * Binds all necessary event handlers for the search results component.
     */
    bindEvents() {
      this.bindTabScrollButtons();
      this.bindTabClickHandlers();
      this.bindOutsideClickHandler();
      this.bindTabsScrollHandler();
    }

    /**
     * Binds click handlers to the tab scroll buttons.
     */
    bindTabScrollButtons() {
      this.boundTabScroll = this.handleTabScroll.bind(this);
      this.tabLeftScrollButton.addEventListener('click', this.boundTabScroll);
      this.tabRightScrollButton.addEventListener('click', this.boundTabScroll);
    }

    /**
     * Binds click handlers to the search result tabs.
     */
    bindTabClickHandlers() {
      this.tabClickHandlers = [];
      this.searchResultTabs.forEach((tab) => {
        const handler = this.createTabClickHandler(tab);
        tab.addEventListener('click', handler);
        this.tabClickHandlers.push({ tab, handler });
      });
    }

    /**
     * Creates a click handler for a search result tab.
     * @param {HTMLElement} tab - The tab element.
     * @returns {Function} The click handler function.
     */
    createTabClickHandler(tab) {
      return () => {
        const tabValue = tab.getAttribute('data-search-result-tab');
        this.renderSearchResultsBySelectedTab(tabValue);
      };
    }

    /**
     * Binds a document-level click handler to detect clicks outside the search results panel.
     * When a click outside is detected, the search results panel is dismissed.
     */
    bindOutsideClickHandler() {
      this.searchResultOutsideClickHandler = this.handleResultOutsideClickHandler.bind(this);
      document.addEventListener('click', this.searchResultOutsideClickHandler);
    }

    /**
     * Binds the scroll event handler to the tabs container.
     * Uses requestAnimationFrame to optimize performance during scroll events.
     */
    bindTabsScrollHandler() {
      this.boundUpdateTabsScroll = this.updateTabsScrollButtonsVisibility.bind(this);
      this.tabsContainer.addEventListener('scroll', this.boundUpdateTabsScroll);
    }

    /**
     * Sets the specified search result item as active and deactivates others.
     * If the specified index is out of bounds, the first item is activated.
     * @param {number} index - The index of the item to activate.
     */
    activeSearchResultItem(index = 0) {
      this.deactivateAllSearchResultItems();

      if (this.searchBarResultItems && this.searchBarResultItems.length > index) {
        this.searchBarResultItems[index].classList.add(this.activeSearchResultItemClass);
      } else if (this.searchBarResultItems && this.searchBarResultItems.length > 0) {
        this.searchBarResultItems[0].classList.add(this.activeSearchResultItemClass);
      }
    }

    /**
     * Deactivates all search result items.
     */
    deactivateAllSearchResultItems() {
      if (this.searchBarResultItems && this.searchBarResultItems.length > 0) {
        this.searchBarResultItems.forEach((item) => {
          item.classList.remove(this.activeSearchResultItemClass);
        });
      }
    }

    /**
     * Cleans up event listeners and references to DOM elements.
     */
    destroyInstances() {
      if (this.tabLeftScrollButton) {
        this.tabLeftScrollButton.removeEventListener('click', this.boundTabScroll);
      }
      if (this.tabRightScrollButton) {
        this.tabRightScrollButton.removeEventListener('click', this.boundTabScroll);
      }
      if (this.tabsContainer) {
        this.tabsContainer.removeEventListener('scroll', this.boundUpdateTabsScroll);
      }
      document.removeEventListener('click', this.searchResultOutsideClickHandler);

      if (this.tabClickHandlers && this.tabClickHandlers.length > 0) {
        this.tabClickHandlers.forEach(({ tab, handler }) => {
          tab.removeEventListener('click', handler);
        });
        this.tabClickHandlers = [];
      }
    }

    /**
     * Updates the visual "active" state of the search result tabs.
     */
    updateSelectedTabStyle(tabValue) {
      this.searchResultTabs.forEach((tab) => {
        const currentTabValue = tab.getAttribute('data-search-result-tab');
        tab.classList.toggle('nhs-n1-c-search-bar-tabs__tab--active', currentTabValue === tabValue);
      });
    }

    /**
     * Renders search results for a given tab/category value.
     * Updates tab styles, filters results by category, and calls renderSearchResults().
     */
    renderSearchResultsBySelectedTab(tabValue) {
      this.updateSelectedTabStyle(tabValue);

      const filteredResults = (tabValue && tabValue.toLowerCase() !== '1')
        ? this.searchResultsState.filter((result) => result &&
          result.SearchBarResultCategoryType &&
          String(result.SearchBarResultCategoryType).toLowerCase() === tabValue.toLowerCase())
        : this.searchResultsState;

      this.renderSearchResults(filteredResults);
    }

    /**
     * Handles a document-level click to determine whether the search results panel
     * should be dismissed when the user clicks outside of it.
     */
    handleResultOutsideClickHandler(event) {
      const isClickInside = this.resultsContainer.contains(event.target);
      if (!isClickInside) {
        this.toggleSearchResults(true, true);
      }
    }

    /**
     * Updates the visibility of the tab scroll buttons based on the current scroll position.
     * Uses requestAnimationFrame to optimize performance during scroll events.
     */
    updateTabsScrollButtonsVisibility() {
      if (!this.tabsContainer || this.pendingTabsScrollVisibilityUpdate) {
        return;
      }

      this.pendingTabsScrollVisibilityUpdate = true;

      requestAnimationFrame(() => {
        this.pendingTabsScrollVisibilityUpdate = false;
        this.updateTabScrollButtonsState();
      });
    }

    /**
     * Determines the visibility state of the tab scroll buttons based on the tabs container's scroll position.
     */
    updateTabScrollButtonsState() {
      const { scrollLeft, scrollWidth, clientWidth } = this.tabsContainer;
      const tolerance = 1;
      const hasOverflow = SearchBarResultHandler.hasHorizontalOverflow(scrollWidth, clientWidth, tolerance);
      const maxScrollLeft = scrollWidth - clientWidth;

      SearchBarResultHandler.updateTabButtonVisibility(
        this.tabLeftScrollButton,
        !hasOverflow || scrollLeft <= tolerance
      );

      SearchBarResultHandler.updateTabButtonVisibility(
        this.tabRightScrollButton,
        !hasOverflow || scrollLeft >= maxScrollLeft - tolerance
      );
    }

    /**
     * Determines if the tabs container has horizontal overflow.
     * @param {number} scrollWidth - The total scrollable width of the container.
     * @param {number} clientWidth - The visible width of the container.
     * @param {number} tolerance - The tolerance level for determining overflow.
     * @returns {boolean} True if there is overflow, false otherwise.
     */
    static hasHorizontalOverflow(scrollWidth, clientWidth, tolerance) {
      return scrollWidth - clientWidth > tolerance;
    }

    /**
     * Updates the visibility of a tab scroll button.
     * @param {HTMLElement} button - The button element to update.
     * @param {boolean} shouldHide - True to hide the button, false to show it.
     */
    static updateTabButtonVisibility(button, shouldHide) {
      if (!button) {
        return;
      }
      const target = button;
      if (target.hidden !== shouldHide) {
        target.hidden = shouldHide;
      }
    }

    /**
     * Handles the click event on a tab scroll button.
     * Scrolls the tabs container left or right based on which button was clicked.
     */
    handleTabScroll(event) {
      if (!this.tabsContainer) {
        return;
      }

      const isLeft = event.currentTarget === this.tabLeftScrollButton;
      const scrollAmount = this.calculateScrollAmount(isLeft);

      this.scrollTabsBy(scrollAmount);
      this.updateTabsScrollButtonsVisibility();
    }

    /**
     * Calculates the amount to scroll the tabs container based on the direction.
     * @param {boolean} isLeft - True if scrolling left, false if scrolling right.
     * @returns {number} The amount to scroll in pixels.
     */
    calculateScrollAmount(isLeft) {
      const scrollStep = Math.round(this.tabsContainer.clientWidth * 0.6);
      return isLeft ? -scrollStep : scrollStep;
    }

    /**
     * Scrolls the tabs container by a specified amount.
     * Uses requestAnimationFrame for smooth scrolling.
     * @param {number} amount - The amount to scroll in pixels (positive or negative).
     */
    scrollTabsBy(amount) {
      requestAnimationFrame(() => {
        this.tabsContainer.scrollLeft += amount;
      });
    }

    /**
     * Toggles the visibility of the search results container.
     * Expects an instance property `resultsContainer` that supports the `hidden` attribute
     * (e.g. an HTMLElement). If `resultsContainer` is not set, the call is a no-op.
     * @param {boolean} isVisible - When true, the results container is shown; when false, it is hidden.
     * @param {boolean} forceHidden - When true, the results container is always hidden regardless of isVisible.
     * @returns {void}
     */
    toggleSearchResults(requestedVisible, forceHidden = false) {
      if (this.resultsContainer) {
        const shouldShow = !forceHidden && (requestedVisible || this.hasSearchText());
        this.resultsContainer.hidden = !shouldShow;

        if (shouldShow) {
          this.updateTabsScrollButtonsVisibility();
        }
      }
    }

    /**
     * Determines if there is any non-whitespace search text present.
     * @returns {boolean} True if there is text, false otherwise.
     */
    hasSearchText() {
      return this.searchBarState.searchText && this.searchBarState.searchText.trim().length > 0;
    }

    /**
     * Toggles the visibility of the search results message container.
     */
    toggleSearchResultsMessageContainer(isVisible) {
      if (this.messageContainer) {
        if (this.dataSearchKeyword && this.searchKeywordContainer) {
          SearchBarResultHandler.updateMessageKeywordText(this.dataSearchKeyword, this.searchBarState.searchText);
        }

        const isMessageVisible = isVisible && this.hasSearchText();
        SearchBarResultHandler.setMessageContainerVisibility(this.messageContainer, isMessageVisible);
      }
    }

    /**
     * Updates the text content of the specified element.
     * If the element is null or undefined, the call is a no-op.
     */
    static updateMessageKeywordText(element, text) {
      if (element) {
        const keywordElement = element;
        keywordElement.textContent = text;
      }
    }

    /**
     * Sets the visibility of the specified message container element.
     * If the element is null or undefined, the call is a no-op.
     */
    static setMessageContainerVisibility(element, isVisible) {
      if (element) {
        const keywordElement = element;
        keywordElement.hidden = !isVisible;
      }
    }

    /**
     * Updates the text content of the first DOM element that has the specified attribute.
     * Commonly used to keep a tab or badge count in sync with search result totals.
     */
    searchResultsUpdateTabCount(dataAttr, value) {
      const tabCountElement = this.searchBarContainer && this.searchBarContainer.querySelector(`[${dataAttr}]`);
      if (tabCountElement) {
        tabCountElement.textContent = value;
      }
    }

    /**
     * Loads and renders search results if there is search text present.
     * If no search text is present, the call is a no-op.
     * If search results are available in the instance state, they are rendered;
     * otherwise, an empty results set is rendered.
     */
    loadSearchResults() {
      if (this.hasSearchText()) {
        if (this.searchResultsState && Array.isArray(this.searchResultsState) && this.searchResultsState.length > 0) {
          this.updateUIState(this.searchResultsState);
        } else {
          this.updateUIState([]);
        }
      }
    }

    /**
     * Updates the UI state with the provided search results.
     * Sets result counts, renders results, updates tab styles, and scrolls tabs to the left.
     */
    updateUIState(searchResultsState) {
      this.setSearchResultsCounts(searchResultsState);
      this.renderSearchResults(searchResultsState);
      this.updateSelectedTabStyle(this.getFirstTabAttribute());
      this.scrollTabsToLeft();
    }

    /**
     * Renders the provided search results into the results container.
     * If no results are provided, shows the "no results" message instead.
     */
    renderSearchResults(results) {
      if (!this.resultsContainer) {
        return;
      }

      const resultsList = this.resultsContainer.querySelector('[data-search-results-list]');
      if (!resultsList) {
        return;
      }

      resultsList.innerHTML = '';

      if (SearchBarHelper.hasListItems(results)) {
        this.toggleSearchResultsMessageContainer(false);
        resultsList.innerHTML = this.generateResultsHTML(results);
        this.bindSearchResultsEvents();
      } else {
        this.toggleSearchResultsMessageContainer(true);
      }

      this.toggleSearchResults(true);
      this.scrollResultsContainerToTop();
      this.activeSearchResultItem(0);
    }

    /**
     * Generates the HTML for the search results.
     * @param {Array} results - The search results to render.
     * @returns {string} The HTML string for the search results.
     */
    generateResultsHTML(results) {
      const textLimit = SearchBarHelper.getTextLimit(this.searchBarState.isMobile);
      const { searchText } = this.searchBarState;

      return results.map(
        (item) => SearchBarHelper.generateSingleResultHTML(item, textLimit, searchText)
      ).join('');
    }

    /**
     * Retrieves the value of the "data-search-result-tab" attribute from the first
     * element in the searchResultTabs collection.
     */
    getFirstTabAttribute() {
      if (this.searchResultTabs && this.searchResultTabs.length > 0) {
        return this.searchResultTabs[0].getAttribute('data-search-result-tab');
      }
      return null;
    }

    /**
     * Scrolls the search results container back to the top.
     * Uses smooth scrolling behavior if supported by the browser.
     * If the container or inner results element is not found, the call is a no-op.
     */
    scrollResultsContainerToTop() {
      const searchResultsContainer = this.searchBarContainer.querySelector('[data-search-result-container]');
      if (searchResultsContainer) {
        requestAnimationFrame(() => searchResultsContainer.scrollTo({ top: 0, behavior: 'smooth' }));
      }
    }

    /**
     * Scrolls the tabs container fully to the left.
     * Uses smooth scroll if supported.
     */
    scrollTabsToLeft() {
      if (this.tabsContainer) {
        requestAnimationFrame(() => {
          this.tabsContainer.scrollTo({ left: 0, behavior: 'smooth' });
        });

        this.updateTabsScrollButtonsVisibility();
      }
    }

    /**
     * Sets the counts for various search result categories based on the provided results array.
     * Updates the corresponding tab or badge elements in the DOM with the new counts.
     * @param {Array} results - The array of search result objects.
     */
    setSearchResultsCounts(results) {
      const categories = {
        total: 'data-total-results-count',
        2: 'data-total-location-count',
        3: 'data-total-communities-count',
        4: 'data-total-builders-count',
        5: 'data-total-articles-count',
      };

      const counts = Object.fromEntries(Object.keys(categories).map((key) => [key, 0]));

      if (Array.isArray(results)) {
        counts.total = results.length;

        results.forEach(({ SearchBarResultCategoryType }) => {
          if (categories[SearchBarResultCategoryType]) {
            counts[SearchBarResultCategoryType] += 1;
          }
        });
      }

      Object.entries(categories).forEach(([key, attr]) => {
        this.searchResultsUpdateTabCount(attr, counts[key]);
      });
    }

    /**
     * Restores the previous definition of SearchBarResultHandler.
     * Useful when integrating with other scripts that might use the same namespace.
     * @returns {SearchBarResultHandler} The current SearchBarResultHandler class
     */
    static noConflict() {
      global.NewHomeSource.SearchBarResultHandler = previousComponent;
      return SearchBarResultHandler;
    }
  }

  global.NewHomeSource.SearchBarResultHandler = SearchBarResultHandler;
  return SearchBarResultHandler;
}));

